import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from 'react';
import { useIntegrationApp } from '@integration-app/react';
import {
  getCampaignAgentIds,
  getCampaignContextParams,
} from 'src/services/campaigns/campaigns.service';
import { getReferencedContextParams } from 'src/interfaces/agent.interface';
import { Agent } from 'src/interfaces/agent.interface';
import useAuthedRequest, { AuthedRequest } from 'src/hooks/useAuthedRequest';
import {
  ContactRecord,
  ListActionAdditionalParams,
  listAction,
  DataSourceKey,
} from 'src/services/integration-app.service';
import { CrmField } from '../components/DraggableCrmField';
import { useCrmSync, CrmSyncStep } from './CrmSyncContext';
import { useCampaignContext } from './CampaignContext';
import { Option } from 'src/components/SelectBox';

// Field types for the mapping
interface CampaignField {
  name: string;
  label: string;
  required: boolean;
  description?: string;
}

interface FieldMappingEntry {
  campaignField: string;
  crmField: string | null;
}

const fetchAgentsById = async (
  agentIds: string[],
  authedRequest: AuthedRequest,
): Promise<Agent[]> => {
  return Promise.all(
    agentIds.map((agentId) => authedRequest.get(`/agents/${agentId}`)),
  );
};

// Types
interface FieldMappingContextValue {
  // State
  fieldMapping: Record<string, string>;
  isFieldMappingValid: boolean;
  availableCrmFields: CrmField[];
  campaignFields: CampaignField[];
  mappingEntries: FieldMappingEntry[];
  isInitialized: boolean;
  searchQuery: string;
  sampleContacts: ContactRecord[];

  // Setters
  setFieldMapping: (mapping: Record<string, string>) => void;
  setIsFieldMappingValid: (isValid: boolean) => void;
  setSearchQuery: (query: string) => void;

  // Actions
  handleFieldMappingChange: (mapping: Record<string, string>) => void;
  handleFieldMappingValidationChange: (isValid: boolean) => void;
  updateFieldMapping: (campaignField: string, crmField: string | null) => void;
  handleRemoveMapping: (campaignField: string) => void;

  // Computed values
  mappedFieldValues: (string | null)[];
  filteredCrmFields: CrmField[];
  requiredFields: CampaignField[];
  optionalFields: CampaignField[];

  // Utility functions
  extractCrmFieldName: (mappingValue: string) => string;
  createTableRows: (
    contacts: ContactRecord[],
    fieldMapping: Record<string, string>,
  ) => Record<string, string>[];
}

const FieldMappingContext = createContext<FieldMappingContextValue | undefined>(
  undefined,
);

export const useFieldMapping = () => {
  const context = useContext(FieldMappingContext);
  if (context === undefined) {
    throw new Error(
      'useFieldMapping must be used within a FieldMappingProvider',
    );
  }
  return context;
};

interface FieldMappingProviderProps {
  children: ReactNode;
}

export const FieldMappingProvider: React.FC<FieldMappingProviderProps> = ({
  children,
}) => {
  const authedRequest = useAuthedRequest();
  const integrationApp = useIntegrationApp();
  const { integratedCrm, additionalParams, currentStep } = useCrmSync();
  const { campaign } = useCampaignContext();

  // State
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});
  const [isFieldMappingValid, setIsFieldMappingValid] = useState(false);
  const [availableCrmFields, setAvailableCrmFields] = useState<CrmField[]>([]);
  const [campaignFields, setCampaignFields] = useState<CampaignField[]>([]);
  const [mappingEntries, setMappingEntries] = useState<FieldMappingEntry[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sampleContacts, setSampleContacts] = useState<ContactRecord[]>([]);

  // Internal function to fetch contacts for field mapping
  const fetchContactsFromCrm = useCallback(
    async (
      integratedCrm: Option | null,
      additionalParams: ListActionAdditionalParams,
      maxContacts?: number,
    ): Promise<ContactRecord[]> => {
      const response = await listAction<ContactRecord>({
        client: integrationApp,
        integrationKey: integratedCrm?.value || null,
        dataSourceKey: DataSourceKey.CONTACTS,
        maxRecords: maxContacts,
        ...additionalParams,
      });

      return response.records;
    },
    [integrationApp],
  );

  // Load campaign fields
  useEffect(() => {
    const loadCampaignFields = async () => {
      if (!campaign || currentStep === CrmSyncStep.configuration) return;

      try {
        const fields: CampaignField[] = [];

        // Always required: phone number
        fields.push({
          name: 'to_number',
          label: 'To number',
          required: true,
          description: 'Phone number for calls and SMS',
        });

        // Required if email campaign is configured
        if (campaign.emailCampaignConfig) {
          fields.push({
            name: 'to_email',
            label: 'To Email',
            required: true,
            description: 'Email address for email communications',
          });
        }

        // Validate campaign structure before proceeding
        if (
          !campaign.initialCallConfig ||
          !campaign.followUpCommunicationConfigs
        ) {
          console.error('Campaign missing required properties:', {
            hasInitialCallConfig: !!campaign.initialCallConfig,
            hasFollowUpCommunicationConfigs:
              !!campaign.followUpCommunicationConfigs,
          });
          throw new Error('Campaign object is missing required properties');
        }

        // Extract context parameters from agents and campaign
        const agentIds = getCampaignAgentIds(campaign);
        const agents = await fetchAgentsById(agentIds, authedRequest);

        let contextParams: string[] = [];

        agents.forEach((agent) => {
          const referencedContextParams = getReferencedContextParams(agent);
          contextParams.push(...referencedContextParams);
        });

        const campaignContextParams = getCampaignContextParams(campaign);
        contextParams = Array.from(
          new Set([...contextParams, ...campaignContextParams]),
        );

        // Add context parameters as optional fields
        contextParams.sort().forEach((param) => {
          fields.push({
            name: `context.${param}`,
            label: param,
            required: false,
            description: 'Optional context parameter',
          });
        });

        setCampaignFields(fields);

        // Initialize field mappings
        const initialMappings = fields.map((field) => ({
          campaignField: field.name,
          crmField: null,
        }));
        setMappingEntries(initialMappings);
      } catch (error) {
        console.error('Failed to load campaign fields:', error);
      }
    };

    loadCampaignFields();
  }, [campaign, currentStep]);

  // Load available CRM fields from sample contacts
  useEffect(() => {
    const extractFieldsFromContacts = () => {
      if (
        !integratedCrm ||
        !sampleContacts.length ||
        currentStep === CrmSyncStep.configuration
      )
        return;

      try {
        const fieldsSet = new Set<string>();
        const fields: CrmField[] = [];

        // Extract all unique field names from sample contacts
        sampleContacts.forEach((contact) => {
          Object.keys(contact.fields).forEach((fieldName) => {
            fieldsSet.add(fieldName);
          });
        });

        // Convert to CrmField objects
        Array.from(fieldsSet).forEach((fieldName) => {
          fields.push({
            value: fieldName,
            label: fieldName,
            description: `Field from ${integratedCrm.label}`,
          });
        });

        // Sort fields alphabetically by label for better UX
        fields.sort((a, b) => a.label.localeCompare(b.label));

        setAvailableCrmFields(fields);
        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to extract CRM fields from contacts:', error);
        setIsInitialized(true);
      }
    };

    extractFieldsFromContacts();
  }, [integratedCrm, sampleContacts, currentStep]);

  // Load sample contact data for preview
  useEffect(() => {
    const loadSampleContacts = async () => {
      if (!integratedCrm || currentStep === CrmSyncStep.configuration) return;

      try {
        const contacts = await fetchContactsFromCrm(
          integratedCrm,
          additionalParams,
          5,
        );
        setSampleContacts(contacts);
      } catch (error) {
        console.error('Failed to load sample contacts:', error);
        setSampleContacts([]);
      }
    };

    if (integratedCrm) {
      loadSampleContacts();
    }
  }, [integratedCrm, additionalParams, currentStep]);

  // Action handlers
  const handleFieldMappingChange = useCallback(
    (mapping: Record<string, string>) => {
      setFieldMapping(mapping);
    },
    [],
  );

  const handleFieldMappingValidationChange = useCallback((isValid: boolean) => {
    setIsFieldMappingValid(isValid);
  }, []);

  // Update mapping when field is dropped
  const updateFieldMapping = useCallback(
    (campaignField: string, crmField: string | null) => {
      setMappingEntries((prevMappings) => {
        const updatedMappings = prevMappings.map((mapping) =>
          mapping.campaignField === campaignField
            ? { ...mapping, crmField }
            : mapping,
        );

        // Convert to mapping object and notify parent
        const mappingObject: Record<string, string> = {};
        updatedMappings.forEach((mapping) => {
          if (mapping.crmField) {
            mappingObject[mapping.campaignField] =
              `{{event.fields.${mapping.crmField}}}`;
          }
        });
        setFieldMapping(mappingObject);

        return updatedMappings;
      });
    },
    [],
  );

  // Separate effect to handle validation when mappings change
  useEffect(() => {
    const requiredFields = campaignFields.filter((field) => field.required);
    const mappedRequiredFields = mappingEntries.filter(
      (mapping) =>
        requiredFields.some((field) => field.name === mapping.campaignField) &&
        mapping.crmField !== null,
    );

    const isValid = mappedRequiredFields.length === requiredFields.length;
    setIsFieldMappingValid(isValid);
  }, [mappingEntries, campaignFields]);

  const handleRemoveMapping = useCallback(
    (campaignField: string) => {
      updateFieldMapping(campaignField, null);
    },
    [updateFieldMapping],
  );

  // Utility functions
  const extractCrmFieldName = useCallback((mappingValue: string): string => {
    // Remove {{event.fields.}} wrapper to get the actual CRM field name
    const match = mappingValue.match(/\{\{event\.fields\.(.+?)\}\}/);
    return match ? match[1] : mappingValue;
  }, []);

  const createTableRows = useCallback(
    (
      contacts: ContactRecord[],
      fieldMapping: Record<string, string>,
    ): Record<string, string>[] => {
      return contacts.map((contact) => {
        const row: Record<string, string> = {};

        Object.entries(fieldMapping).forEach(
          ([campaignField, mappingValue]) => {
            const crmFieldName = extractCrmFieldName(mappingValue);
            const fieldValue =
              contact.fields[crmFieldName as keyof typeof contact.fields];
            row[campaignField] = fieldValue ? String(fieldValue) : '-';
          },
        );

        return row;
      });
    },
    [extractCrmFieldName],
  );

  // Computed values
  const mappedFieldValues = mappingEntries
    .filter((mapping) => mapping.crmField !== null)
    .map((mapping) => mapping.crmField);

  const filteredCrmFields = availableCrmFields.filter(
    (field) =>
      !mappedFieldValues.includes(field.value) &&
      (field.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        field.value.toLowerCase().includes(searchQuery.toLowerCase())),
  );

  const requiredFields = campaignFields.filter((field) => field.required);
  const optionalFields = campaignFields.filter((field) => !field.required);

  const contextValue = {
    // State
    fieldMapping,
    isFieldMappingValid,
    availableCrmFields,
    campaignFields,
    mappingEntries,
    isInitialized,
    searchQuery,
    sampleContacts,

    // Computed values
    mappedFieldValues,
    filteredCrmFields,
    requiredFields,
    optionalFields,

    // Utility functions
    extractCrmFieldName,
    createTableRows,

    // Setters
    setFieldMapping,
    setIsFieldMappingValid,
    setSearchQuery,

    // Actions
    handleFieldMappingChange,
    handleFieldMappingValidationChange,
    updateFieldMapping,
    handleRemoveMapping,
  };

  return (
    <FieldMappingContext.Provider value={contextValue}>
      {children}
    </FieldMappingContext.Provider>
  );
};
